import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { Observable, EMPTY, from } from 'rxjs';
import { HeaderTokenService, UserInfo, UserInfoService } from '@msi/commandcentral-common-header';
import { ORCHESTRATE_SCOPE } from '../../../environments/environment';
import { environment as env } from 'src/environments/environment';
import { LoadingErrorsService } from '../loading-errors/loading-errors.service';
import { ServiceInfo } from '@msi/commandcentral-common-header';
import { UserPermissions } from './user-permissions/user-permissions';

declare let pendo: any;

@Injectable({
	providedIn: 'root',
})
export class CcAdminApiHelperService {
	public tokenReady = this.headerTokenService.tokenReady;
	private agencyId: string;

	constructor(
		private headerTokenService: HeaderTokenService,
		private loadingErrorsService: LoadingErrorsService,
		private userInfoService: UserInfoService
	) {
		// For developer convenience, expose a console function to obtain the user token
		(window as any).getToken = () => {
			console.log(`Token is for Agency: ${this.agencyId}`);
			console.log(this.headerTokenService.getTokenSync());
		};

		// Pre-initialize Pendo
		(() => {
			((p, e, n, d, o) => {
				o = p[d] = p[d] || {};
				o._q = o._q || [];
				const v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
				for (const w of v) {
					(m => {
						o[m] =
							o[m] ||
							(() => {
								// eslint-disable-next-line prefer-rest-params
								o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
							});
					})(w);
				}
				const y: any = e.createElement(n);
				y.async = !0;
				y.src = env.pendoJsFilePath;
				const z = e.getElementsByTagName(n)[0];
				z.parentNode.insertBefore(y, z);
			})(window, document, 'script', 'pendo');
		})();
	}

	/**
	 * Verifies user can access Orchestrate and parses user info
	 */
	isAuthorized(): Observable<UserPermissions> {
		return from(this.userInfoService.getUserInfo()).pipe(
			map(userInfo => {
				// Check if the user has permission to access Orchestrate
				const orchestrateService = userInfo.services.find((s: ServiceInfo) => s.systemName === ORCHESTRATE_SCOPE);
				if (!orchestrateService) {
					this.loadingErrorsService.openErrorPage(
						'403',
						$localize`Forbidden`,
						$localize`You are not authorized to access Orchestrate. Make sure you are using the correct agency and are assigned a role with Orchestrate access. If you believe this is an error, contact your agency administrator.`
					);
				}

				// Populate Orchestrate user permissions
				const orchPermissions = orchestrateService.permissions.map(p => p.systemName);
				const user = new UserPermissions(userInfo, orchPermissions);
				this.agencyId = user.agencyId;
				if (!user.canView) {
					this.loadingErrorsService.openErrorPage(
						'403',
						$localize`Unauthorized`,
						$localize`You do not have a role with view permissions for this agency. An agency administrator will be able to assign you to a role.`
					);
				}

				// Initialize Pendo
				if (env.pendoEnabled) {
					pendo.initialize({
						visitor: {
							id: userInfo.email,
						},
						account: {
							id: userInfo.agency.id,
						},
					});
				}

				return user;
			}),
			catchError(err => {
				this.loadingErrorsService.openErrorPage(
					'401',
					err?.statusText ? err.statusText : $localize`Unauthorized`,
					$localize`Failed to verify permissions with Command Central. If issues persist, contact your system administrator.`
				);
				return EMPTY;
			})
		);
	}

	/**
	 * @returns a promise which resolves with the user's info
	 */
	getUserInfo(): Promise<UserInfo> {
		return this.userInfoService.getUserInfo();
	}

	/**
	 * Returns the JWT user token from the HeaderTokenService
	 */
	getToken(): string {
		return this.headerTokenService.getTokenSync();
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
